import useProfile from "@/hooks/use-profile";
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Text, Title } from "rizzui";
import PaymentRepository from "../../utilities/repositories/Payment";
import { IXeroPayment } from "../../utilities/types/Payment";
import { LoadingSpinner } from "../ui/file-upload/upload-zone";
import PaymentPrice from "./payment-verify/payment-price";
import { routes } from "@/config/routes";

interface PendingPaymentNotificationProps {
  pendingPayment: IXeroPayment;
  onCancel: () => void;
  onContinue: () => void;
}

const PendingPaymentNotification: React.FC<PendingPaymentNotificationProps> = ({
  pendingPayment,
  onCancel,
  onContinue,
}) => {
  const [cancelConfirmation, setCancelConfirmation] = useState<string>("");
  const CANCEL_CONFIRMATION_TEXT = "CANCEL PAYMENT";

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md mx-auto bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <Title as="h3" className="mb-4 text-xl font-bold text-gray-900">
            Pending Payment Found
          </Title>

          <div className="mb-6">
            <Text className="mb-4 text-gray-600">
              You have a pending payment for the{" "}
              <strong>{pendingPayment.package?.name}</strong> package.
            </Text>

            {pendingPayment.package?.price && (
              <PaymentPrice
                payment={pendingPayment}
                className="mb-4 p-4 bg-gray-50 rounded-lg"
              />
            )}

            <Text className="text-amber-600 font-medium mb-4">
              If you don't continue with this payment, any amount already paid
              may not be refunded.
            </Text>

            <div className="mt-6 mb-4">
              <Text className="text-gray-700 mb-2">
                To cancel, type <strong>{CANCEL_CONFIRMATION_TEXT}</strong> to
                confirm:
              </Text>
              <Input
                type="text"
                placeholder={`Type ${CANCEL_CONFIRMATION_TEXT} to confirm`}
                value={cancelConfirmation}
                onChange={(e) => setCancelConfirmation(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex justify-between gap-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={cancelConfirmation !== CANCEL_CONFIRMATION_TEXT}
            >
              Cancel Payment
            </Button>
            <Button variant="solid" className="flex-1" onClick={onContinue}>
              Continue Payment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface IAdminPricingPlansWrapperProps {
  children:
    | React.ReactNode
    | ((pendingPayment: IXeroPayment | null) => React.ReactNode);
}

export default function AdminPricingPlansWrapper({
  children,
}: IAdminPricingPlansWrapperProps) {
  const [pendingPayment, setPendingPayment] = useState<IXeroPayment | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [showPaymentNotice, setShowPaymentNotice] = useState<boolean>(true);

  const { profile } = useProfile();

  const navigate = useNavigate();
  const checkPendingPayment = async () => {
    try {
      const response = await PaymentRepository.getPaymentIsPendingByUser();
      const payment = response.data;
      setPendingPayment(payment);
    } catch (error) {
      console.error("Error checking pending payment:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!profile) return;
    if ("isAllPackageExpired" in profile && !profile.isAllPackageExpired) {
      navigate(routes.home);
    }
    checkPendingPayment();
  }, [profile]);

  const handleContinuePayment = () => {
    setShowPaymentNotice(false);
  };

  const handleCancelPayment = async () => {
    try {
      await PaymentRepository.rejectPayment(
        pendingPayment?.xeroInvoiceID || ""
      );
      setPendingPayment(null);
      setShowPaymentNotice(false);
    } catch (error) {
      console.error("Error canceling payment:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  // If there's a pending payment and we should show the notice, render the payment notification
  if (pendingPayment && showPaymentNotice) {
    return (
      <PendingPaymentNotification
        pendingPayment={pendingPayment}
        onCancel={handleCancelPayment}
        onContinue={handleContinuePayment}
      />
    );
  }

  // If there's no pending payment or the user chose to continue, render the children
  return (
    <>{typeof children === "function" ? children(pendingPayment) : children}</>
  );
}
